import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import type { Nil } from '@pbt/pbt-ui-components'

import {
  isChewyPharmacistDeclined,
  type RxUsageStateLike,
} from '~/components/dashboard/clients/utils/orderStatusChipUtils'
import { PrescriptionStates, PrescriptionType } from '~/constants/prescription'
import {
  getDrugDeliveryMethod,
  getDrugStrengthColor,
  getDrugStrengthUnit,
  getInventoryProductSizeUnit,
  getPackageType,
  getPrescriptionProductSizeUnit,
  getPrescriptionStates,
  getProductForm,
  getProductSizeUnit,
  getRetailOrderStates,
} from '~/store/reducers/constants'
import type { RxCardItem } from '~/types'
import type {
  Prescription,
  PrescriptionV2,
} from '~/types/entities/prescriptions'
import { useCurrentBusinessTimezone } from '~/utils/useCurrentBusinessTimezone'

import { getOrderTableFields } from './getOrderTableFields'
import type { OrderTableContent } from './orderTableTypes'
import {
  appendShippingColumn,
  computeRelativeTime,
  type DisplayField,
  getInstructionsField,
  getOrderTableSubHeader,
  getPrescriptionFlags,
  getPrescriptionStateName,
  getRxFields,
  isOrderedDrugCard,
  shouldSuppressFields,
} from './rxCardUtilities'
import {
  getShippingRowTexts,
  type RxUsagesWithShipmentsData,
} from './useRxOrderTableShipments'

function applyChewyOrderTableEnhancements(
  orderTableData: OrderTableContent,
  rxUsagesData: RxUsagesWithShipmentsData | Nil,
  shippingHeader: string,
): OrderTableContent {
  const usages = rxUsagesData?.rxUsages
  let data = orderTableData
  if (usages?.length) {
    data = {
      ...data,
      cardHeaders: data.cardHeaders.map((h, i) => ({
        ...h,
        orderNumber:
          usages[i]?.rxOrder?.externalOrderId ?? h.orderNumber ?? undefined,
        rxNo: rxUsagesData?.rxNo ?? h.rxNo ?? undefined,
      })),
    }
  }
  if (
    data.cardHeaders.some((h, i) => {
      const usage = usages?.[i] ?? null
      const rxUsageState =
        usage && typeof usage === 'object'
          ? (usage as { rxUsageState?: RxUsageStateLike | null }).rxUsageState
          : null
      return isChewyPharmacistDeclined({
        orderDisplayStatus: h.status,
        rxUsageState,
      })
    })
  ) {
    return data
  }
  const rawShippingTexts = rxUsagesData
    ? getShippingRowTexts(rxUsagesData, true)
    : []
  const rowCount = data.rows.length
  const shippingTexts =
    rawShippingTexts.length >= rowCount
      ? rawShippingTexts.slice(0, rowCount)
      : rawShippingTexts
  return appendShippingColumn(data, shippingTexts, shippingHeader)
}

interface UseRxContentOptions {
  itemDate?: string
  onDispenseRefill?: () => void
  /** Loaded prescription with refills/full data when order table is expanded (used when not building from rxUsages). */
  prescriptionForQtyAndShipping?: PrescriptionV2 | null
  rxUsagesData?: RxUsagesWithShipmentsData | null
}

export interface RxContent {
  fields: DisplayField[]
  instructions: DisplayField
  isCustomView: boolean
  isVetApproved: boolean
  orderTableData: OrderTableContent | null
  orderTableSubHeader: string | null
  orderTableTitle: string
  prescriptionStateName: string | null
  showTime: string | null
}

const EMPTY_RX_CONTENT: RxContent = {
  fields: [],
  instructions: { label: '', value: '' },
  isCustomView: false,
  isVetApproved: false,
  orderTableData: null,
  orderTableSubHeader: null,
  orderTableTitle: '',
  prescriptionStateName: null,
  showTime: null,
}

/**
 * Hook that fetches Redux data and returns Rx fields and instructions for the card.
 */
export const useRxCardContent = (
  item: RxCardItem | Nil,
  options?: UseRxContentOptions,
): RxContent => {
  const prescription: Prescription | Nil = item?.prescription
  const isOrderedDrug = item ? isOrderedDrugCard(item) : false
  const {
    itemDate,
    onDispenseRefill,
    prescriptionForQtyAndShipping,
    rxUsagesData,
  } = options ?? {}

  const prescriptionStates = useSelector(getPrescriptionStates)
  const retailOrderStates = useSelector(getRetailOrderStates)
  const prescriptionProductSizeUnit = useSelector(
    getPrescriptionProductSizeUnit,
  )
  const productSizeUnit = useSelector(getProductSizeUnit)
  const inventoryProductSizeUnit = useSelector(getInventoryProductSizeUnit)
  const packageType = useSelector(getPackageType)
  const productForm = useSelector(getProductForm)
  const drugDeliveryMethod = useSelector(getDrugDeliveryMethod)
  const drugStrengthColor = useSelector(getDrugStrengthColor)
  const drugStrengthUnit = useSelector(getDrugStrengthUnit)

  const vetDietConstants =
    inventoryProductSizeUnit && packageType && productForm
      ? {
          InventoryProductSizeUnit: inventoryProductSizeUnit,
          PackageType: packageType,
          ProductForm: productForm,
        }
      : undefined

  const timezone = useCurrentBusinessTimezone()
  const { t } = useTranslation()

  if (!prescription) return EMPTY_RX_CONTENT

  const { prescriptionType } = prescription

  const flags = getPrescriptionFlags(
    prescription,
    prescriptionType,
    prescriptionStates,
  )

  const shouldEnableDispenseRefill =
    prescriptionType === PrescriptionType.IN_HOUSE && !flags.isCanceled

  const fields = getRxFields(prescription, prescriptionType, {
    isOrderedDrug,
    prescriptionStates,
    prescriptionProductSizeUnit,
    productSizeUnit,
    vetDietConstants,
    onDispenseRefill: shouldEnableDispenseRefill ? onDispenseRefill : undefined,
    prescriptionForRefillsRemainingAndLastFill:
      prescriptionType === PrescriptionType.CHEWY &&
      prescriptionForQtyAndShipping
        ? prescriptionForQtyAndShipping
        : undefined,
  })

  const instructions = getInstructionsField(prescription)
  const prescriptionStateName = getPrescriptionStateName(
    prescription,
    prescriptionStates,
  )
  const isVetApproved = prescriptionStateName === PrescriptionStates.APPROVED

  const isCustomView = shouldSuppressFields(flags)
  const isChewy = prescriptionType === PrescriptionType.CHEWY

  const showTime =
    isCustomView && itemDate ? computeRelativeTime(itemDate, timezone) : null

  const orderTableSubHeader = getOrderTableSubHeader(
    prescription,
    prescriptionType,
  )
  const orderTableTitle: string = t('Common:ORDERS')

  const baseOrderTableData = isCustomView
    ? null
    : getOrderTableFields(prescription, prescriptionType, {
        isOrderedDrug,
        orderTableLoadedRx: prescriptionForQtyAndShipping ?? undefined,
        prescriptionProductSizeUnit,
        productSizeUnit,
        prescriptionStates,
        retailOrderStates,
        vetDietConstants,
        DrugDeliveryMethod: drugDeliveryMethod,
        DrugStrengthColor: drugStrengthColor,
        DrugStrengthUnit: drugStrengthUnit,
        InventoryProductSizeUnit: inventoryProductSizeUnit,
        ProductForm: productForm,
        rxUsagesData: isChewy ? rxUsagesData : undefined,
        timezone,
      })

  const shippingHeader = t('Common:SHIPPING')
  const alreadyHasShipping =
    baseOrderTableData?.headers?.includes(shippingHeader) ?? false
  const orderTableData =
    baseOrderTableData && isChewy && rxUsagesData && !alreadyHasShipping
      ? applyChewyOrderTableEnhancements(
          baseOrderTableData,
          rxUsagesData,
          shippingHeader,
        )
      : baseOrderTableData

  return {
    fields,
    instructions,
    isCustomView,
    isVetApproved,
    orderTableData,
    orderTableSubHeader,
    orderTableTitle,
    prescriptionStateName,
    showTime,
  }
}
